'use strict';

var classValidator = require('class-validator');
var translation = require('@templafy/translation');
var keys = require('./keys.js');
var fieldMetadata = require('./fieldMetadata.js');
var validationPointers = require('./validationPointers.js');

/* eslint-disable no-console */
function translatedMessage(validationMessage) {
    if (validationMessage == null) {
        return undefined;
    }
    if (validationMessage.translationKey == null) {
        return validationMessage.message;
    }
    const translationArguments = validationMessage.arguments || {};
    try {
        return translation.unsafe_dynamicTranslate(validationMessage.translationKey, translationArguments, validationMessage.message);
    }
    catch (error) {
        if (process.env.NODE_ENV === 'development') {
            console.group('Development error:');
            if (error instanceof Error) {
                console.error(error.message);
            }
            console.warn('Translation arguments variables:', translationArguments);
            console.info('Validation translation key:', validationMessage.translationKey);
            console.info('using fallback message:', validationMessage.message);
            console.groupCollapsed('Original error:');
            console.error(error);
            console.groupEnd();
            console.groupEnd();
        }
        return validationMessage.message;
    }
}
function initializeValidationState(validationClass, initialValues, customValidation, initialLoadingValues = false) {
    const values = fieldMetadata.convertValuesToClass(validationClass, initialValues);
    const serverValidationPointers = {};
    const clientValidationPointers = validateValues(values, customValidation);
    const validationPointers$1 = validationPointers.mergeValidationPointers(serverValidationPointers, clientValidationPointers);
    const fieldsMetadata = fieldMetadata.getMetadata(validationClass);
    return {
        loadingValues: initialLoadingValues,
        loadedValues: false,
        loadValuesFailed: null,
        loadValuesError: null,
        fieldsMetadata,
        disableSubmit: false,
        validationPointers: validationPointers$1,
        clientValidationPointers,
        serverValidationPointers,
        hasErrors: keys.keys(validationPointers$1).length > 0,
        fields: createValidationStateFields(values, validationPointers$1, fieldsMetadata, () => false),
        values: values,
    };
}
function validateValues(values, customValidation) {
    const validationErrors = classValidator.validateSync(values);
    const validationPointers$1 = validationPointers.createValidationPointersFromClassValidator(validationErrors);
    if (customValidation != null) {
        const addValidation = (fieldName, message) => {
            validationPointers.addValidationPointerMessages(fieldName, [{ message }], validationPointers$1);
        };
        customValidation(values, addValidation);
    }
    return validationPointers$1;
}
const createValidationStateFields = (values, validationPointers$1, fieldsMetadata, isDirty) => (Object.assign({}, keys.keys(values).reduce((memo, fieldName) => {
    const metadata = fieldsMetadata[fieldName] || {};
    const pointerMessages = validationPointers.getValidationPointerMessages(fieldName, validationPointers$1);
    return Object.assign(Object.assign({}, memo), { [fieldName]: Object.assign(Object.assign({}, metadata), { isDirty: isDirty(fieldName), messages: pointerMessages, message: translatedMessage(pointerMessages[0]) }) });
}, {})));
function hasDirtyFieldErrors(validationStateFields) {
    const fields = Object.values(validationStateFields);
    for (const field of fields) {
        if (field.isDirty && field.messages.length > 0) {
            return true;
        }
    }
    return false;
}
function createReducer(validationModel, customValidation) {
    return function reducer(state, action) {
        var _a;
        switch (action.type) {
            case 'loadValues': {
                return Object.assign(Object.assign({}, state), { loadingValues: true, loadedValues: false, loadValuesError: null });
            }
            case 'loadedValues': {
                const newValues = fieldMetadata.convertValuesToClass(validationModel, action.values);
                const clientValidationPointers = validateValues(newValues, customValidation);
                const validationPointers$1 = validationPointers.mergeValidationPointers(state.serverValidationPointers, clientValidationPointers);
                const hasErrors = keys.keys(validationPointers$1).length > 0;
                const fields = createValidationStateFields(newValues, validationPointers$1, state.fieldsMetadata, () => false);
                return Object.assign(Object.assign({}, state), { loadingValues: false, loadedValues: true, loadValuesError: null, fields, values: newValues, hasErrors,
                    clientValidationPointers });
            }
            case 'loadValuesFailed': {
                return Object.assign(Object.assign({}, state), { loadingValues: false, loadedValues: false, loadValuesError: action.error });
            }
            case 'setValue': {
                const newValues = fieldMetadata.convertValuesToClass(validationModel, state.values);
                const { type } = state.fieldsMetadata[action.fieldName];
                Object.assign(newValues, state.values, {
                    [action.fieldName]: fieldMetadata.convertValueToClassValue(type, action.value),
                });
                const serverValidationPointers = Object.assign({}, state.serverValidationPointers);
                delete serverValidationPointers[action.fieldName];
                const clientValidationPointers = validateValues(newValues, customValidation);
                const validationPointers$1 = validationPointers.mergeValidationPointers(serverValidationPointers, clientValidationPointers);
                const hasErrors = keys.keys(validationPointers$1).length > 0;
                const fields = createValidationStateFields(newValues, validationPointers$1, state.fieldsMetadata, (fieldName) => fieldName === action.fieldName
                    ? true
                    : state.fields[fieldName].isDirty);
                return Object.assign(Object.assign({}, state), { disableSubmit: hasErrors && hasDirtyFieldErrors(fields), hasErrors,
                    validationPointers: validationPointers$1,
                    clientValidationPointers,
                    serverValidationPointers, values: newValues, fields });
            }
            case 'submit': {
                const serverValidationPointers = (_a = action.serverValidationPointers) !== null && _a !== void 0 ? _a : state.serverValidationPointers;
                const validationPointers$1 = validationPointers.mergeValidationPointers(serverValidationPointers, state.clientValidationPointers);
                const hasErrors = keys.keys(validationPointers$1).length > 0;
                return Object.assign(Object.assign({}, state), { hasErrors, disableSubmit: hasErrors, validationPointers: validationPointers$1,
                    serverValidationPointers, fields: createValidationStateFields(state.values, validationPointers$1, state.fieldsMetadata, () => true) });
            }
            default: {
                return state;
            }
        }
    };
}

exports.createReducer = createReducer;
exports.createValidationStateFields = createValidationStateFields;
exports.hasDirtyFieldErrors = hasDirtyFieldErrors;
exports.initializeValidationState = initializeValidationState;
exports.translatedMessage = translatedMessage;
exports.validateValues = validateValues;
//# sourceMappingURL=validationState.js.map
